import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useMemo, useEffect, useState } from 'react'
import { useCurrentSchedule } from './useCurrentSchedule'

export const useCreditsToGenerateContent = (
  socialCount: number,
  blogAndEmailCount: number,
  shortFormVideoCount: number,
) => {
  const profile = useBrandProfile({ cacheTime: 5 * 60 * 1000 })
  const hasAnimatedImage = !!profile?.data?.brand_kit?.content_preferences?.video_sources?.includes('animated_image')
  const hasStylizedImage = !!profile?.data?.brand_kit?.content_preferences?.apply_visual_style
  const hasMultipleFeaturedMedia = (profile?.data?.brand_kit?.content_preferences?.featured_media?.length || 0) > 1
  const hasFeaturedImage = !!profile?.data?.brand_kit?.content_preferences?.featured_media?.includes('image')
  const hasFeaturedVideo = !!profile?.data?.brand_kit?.content_preferences?.featured_media?.includes('video')
  const aiVideoWeight = (profile?.data?.brand_kit?.content_preferences?.video_source_animated_image_weight || 0) / 100
  const brandKitStockVideoWeight =
    (profile?.data?.brand_kit?.content_preferences?.video_source_brand_kit_stock_weight || 0) / 100

  const {
    creditUsageStore: { creditUsage },
    tierUsageStore: { currentSubscription, autopilotAddOns },
  } = useRootStore()
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    (addon) => addon.key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const isOnPioneer = currentSubscription?.plan === PaymentPlanKey.VirtualMarketer
  const isTrialing = currentSubscription?.status === 'trialing'
  const usageCosts = creditUsage?.usage_costs
  const contentGenerationCost = isOnPioneer ? 0 : usageCosts?.content_credits_cost || 0
  const visualStylesCost = usageCosts?.visual_styles_credits_cost || 0
  const animatedImagesCost = usageCosts?.video_credits_cost || 0

  const [isProcessing, setIsProcessing] = useState(true)

  const { needsCredits, creditsNeeded, creditsRemaining } = useMemo(() => {
    if (!creditUsage || Object.keys(creditUsage).length === 0) {
      return { needsCredits: false, creditsNeeded: 0, creditsRemaining: 0 }
    }

    let socialCountCreditsNeeded = 0
    let shortFormVideoCountCreditsNeeded = 0
    let blogAndEmailCountCreditsNeeded = blogAndEmailCount * contentGenerationCost
    if (hasStylizedImage) {
      blogAndEmailCountCreditsNeeded += blogAndEmailCount * 3 * visualStylesCost
    }

    // socialCounts cost calculation
    if (hasMultipleFeaturedMedia) {
      if (hasFeaturedImage) {
        if (hasStylizedImage) {
          socialCountCreditsNeeded += Math.round(socialCount * 0.5) * visualStylesCost
        } else {
          socialCountCreditsNeeded += Math.round(socialCount * 0.5) * contentGenerationCost
        }
      }
      if (hasFeaturedVideo) {
        if (hasAnimatedImage) {
          socialCountCreditsNeeded += Math.round(socialCount * 0.5) * animatedImagesCost
          if (hasStylizedImage) {
            shortFormVideoCountCreditsNeeded += Math.round(shortFormVideoCount * 0.5) * visualStylesCost
          }
        } else {
          socialCountCreditsNeeded += Math.round(socialCount * 0.5) * contentGenerationCost
        }
      }
    } else if (hasFeaturedImage && hasStylizedImage) {
      socialCountCreditsNeeded = socialCount * visualStylesCost
    } else if (hasFeaturedVideo && hasAnimatedImage) {
      socialCountCreditsNeeded = socialCount * animatedImagesCost
    } else {
      socialCountCreditsNeeded = socialCount * contentGenerationCost
    }

    // Short form video cost calculation
    if (hasAnimatedImage) {
      shortFormVideoCountCreditsNeeded = Math.round(shortFormVideoCount * aiVideoWeight) * animatedImagesCost

      if (hasStylizedImage) {
        shortFormVideoCountCreditsNeeded += Math.round(shortFormVideoCount * aiVideoWeight) * visualStylesCost
      }
      shortFormVideoCountCreditsNeeded +=
        Math.round(shortFormVideoCount * brandKitStockVideoWeight) * contentGenerationCost
    } else {
      shortFormVideoCountCreditsNeeded = shortFormVideoCount * contentGenerationCost
    }

    const creditsNeeded = socialCountCreditsNeeded + blogAndEmailCountCreditsNeeded + shortFormVideoCountCreditsNeeded
    const creditsRemaining = creditUsage.credits_remaining || 0

    let needsCredits = creditsNeeded > 0 && creditsNeeded > creditsRemaining

    if (isOnPioneer) {
      needsCredits = needsCredits && !isTrialing && (hasAnimatedImage || hasStylizedImage)
    }

    if (creditUsage.can_auto_reload) {
      needsCredits = false
    }

    return { needsCredits, creditsNeeded, creditsRemaining }
  }, [
    socialCount,
    blogAndEmailCount,
    shortFormVideoCount,
    creditUsage,
    isOnPioneer,
    isTrialing,
    hasAnimatedImage,
    hasStylizedImage,
    animatedImagesCost,
    contentGenerationCost,
    hasFeaturedImage,
    hasFeaturedVideo,
    hasMultipleFeaturedMedia,
    visualStylesCost,
    aiVideoWeight,
    brandKitStockVideoWeight,
  ])

  useEffect(() => {
    // Consider processing complete when we have all the data and the computed values are ready
    const hasRequiredData = profile.isFetched && creditUsage && Object.keys(creditUsage).length > 0
    if (hasRequiredData) {
      setIsProcessing(false)
    }
  }, [profile.isFetched, creditUsage, needsCredits, creditsNeeded, creditsRemaining])

  return {
    needsCredits,
    creditsNeeded,
    creditsRemaining,
    autopilotContentCreditsPrice,
    isProcessing,
  }
}

export const useNeedsCreditsToGenerateContent = (
  socialCount?: number,
  blogAndEmailCount?: number,
  shortFormVideoCount?: number,
) => {
  const {
    socialCount: scheduleSocialCount,
    blogAndEmailCount: scheduleBlogAndEmailCount,
    shortFormVideoCount: scheduleShortFormVideoCount,
  } = useCurrentSchedule()
  return useCreditsToGenerateContent(
    socialCount ?? scheduleSocialCount,
    blogAndEmailCount ?? scheduleBlogAndEmailCount,
    shortFormVideoCount ?? scheduleShortFormVideoCount,
  )
}
